import Analytics from '@/utils/analytics';
import Animated, {
  Easing,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import React, { useEffect } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { TouchableOpacity } from 'react-native';
import { TrackingData } from '@/utils/analytics/base-reporter';

export interface Props {
  activeTrackColor?: string;
  onValueChange: (newValue: boolean) => void;
  testID?: string;
  trackingData: TrackingData | null;
  value: boolean;
}

const SWITCH_MARGIN = 4;
const SWITCH_SIZE = 24;
const TRACK_WIDTH = 56;
const TRACK_HEIGHT = 32;
const SWITCH_ON_POSITION = TRACK_WIDTH - SWITCH_SIZE - SWITCH_MARGIN;
const SWITCH_OFF_POSITION = SWITCH_MARGIN;
const ANIMATION_DURATION = 200;

export const Switch: React.FC<Props> = ({
  activeTrackColor,
  onValueChange,
  testID,
  trackingData,
  value,
}) => {
  const [styles, theme] = useStyles();

  const enabledTrackColor = activeTrackColor || theme.colors.pepperGreen;

  const switchAnimationValue = useSharedValue(value ? 1 : 0);

  useEffect(() => {
    switchAnimationValue.value = withTiming(value ? 1 : 0, {
      duration: ANIMATION_DURATION,
      easing: Easing.linear,
    });
  }, [switchAnimationValue, value]);

  const animatedBackdropStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      switchAnimationValue.value,
      [0, 1],
      [theme.colors.stroke, enabledTrackColor],
    ),
  }));

  const animatedCircleStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateX: interpolate(
          switchAnimationValue.value,
          [0, 1],
          [SWITCH_OFF_POSITION, SWITCH_ON_POSITION],
        ),
      },
    ],
  }));

  return (
    <TouchableOpacity
      activeOpacity={1}
      onPress={() => {
        if (trackingData) {
          Analytics.track(
            `${trackingData.name}.change`,
            trackingData.properties,
          );
        }
        onValueChange(!value);
      }}
      testID={testID}
    >
      <Animated.View style={[styles.switchBackdrop, animatedBackdropStyle]}>
        <Animated.View style={[styles.circle, animatedCircleStyle]} />
      </Animated.View>
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  circle: {
    backgroundColor: theme.colors.white,
    borderRadius: SWITCH_SIZE,
    height: SWITCH_SIZE,
    width: SWITCH_SIZE,
  },
  switchBackdrop: {
    borderRadius: TRACK_HEIGHT,
    height: TRACK_HEIGHT,
    justifyContent: 'center',
    width: TRACK_WIDTH,
  },
}));
